import { afterNextRender, Component, ElementRef, EventEmitter, Injector, Input, OnDestroy, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyConfig } from 'ngx-currency';
import { Subscription, merge } from 'rxjs';

import { ConfirmModalComponent } from '../../../../../shared/components/confirm-modal/confirm-modal.component';
import { VdLiberacaoDialogComponent, VdLiberacaoDialogData, VdLiberacaoDialogResult } from '../vd-liberacao-dialog/vd-liberacao-dialog.component';
import { bloqueioDeRemocao } from '../vd-liberacao-dialog/venda-liberacao.regras';
import { VdEstoqueDialogComponent, VdEstoqueDialogData } from './vd-estoque-dialog/vd-estoque-dialog.component';
import { VdItemDialogComponent, VdItemDialogData } from './vd-item-dialog/vd-item-dialog.component';
import { VdKitDialogComponent, VdKitDialogData } from './vd-kit-dialog/vd-kit-dialog.component';
import { VdMultiploDialogComponent, VdMultiploDialogData } from './vd-multiplo-dialog/vd-multiplo-dialog.component';
import { RegraDoMultiplo, regraDoMultiplo } from './venda-item.multiplo';
import { localizacaoEstoqueParaConsultaDeSaldo, validarSaldoNoLancamento } from './venda-item.estoque';
import { motivoDeExcessoDeLoteSerie } from './venda-item.lote-serie';
import { VdItensListaComponent } from './vd-itens-lista/vd-itens-lista.component';
import { ProdutoEscolhido, VdProdutoBuscaDialogComponent, VdProdutoBuscaDialogData } from './vd-produto-busca-dialog/vd-produto-busca-dialog.component';
import { ContextoDePreco, aplicarListaNoItem, montarFormItemVenda, preencherDoProduto } from './venda-item.form';
import { montarItemDoServidor } from './venda-item.mapa';
import { PlanoDoKit, aplicarComponenteNaLinha, linhasDoKit, proximoNumeroKit, sincronizarPrecoKitPorSoma } from './venda-item.kit';
import { CodigoCompostoResolvidoStruct } from '../../../../../shared/services/structs/produto/codigo-composto-resolvido.struct';
import { TipoIdentificadorCodigoCompostoProdutoEnum } from '../../../../../shared/enums/produto/tipo-identificador-codigo-composto-produto.enum';
import { VendedorVendaStruct } from '../../../../../shared/services/structs/vendas/vendedor-venda.struct';
import { percentualDaComissao } from './venda-item.comissao';
import { recalcularValoresDoItem } from './venda-item.calculo';
import { ratearDescontoGlobal } from '../vd-totais/venda-totais.calculo';
import { UtilService } from '../../../../../shared/services/util.service';
import { ProdutoListStruct } from '../../../../../shared/services/structs/produto/produto-list.struct';
import { ProdutoListaPrecoVigenteStruct } from '../../../../../shared/services/structs/produto/produto-lista-preco-vigente.struct';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { LookupService as ProdutoLookupService } from '../../../../../shared/services/apis/produto/produto-lookup.service';
import { KanbanCor } from '../../../../../shared/services/models/vendas/kanban-cor.model';
import { LocalizacaoEstoque } from '../../../../../shared/services/models/produto/localizacao-estoque.model';
import { VendaItemRastreabilidade } from '../../../../../shared/services/models/vendas/venda-item-rastreabilidade.model';
import { VendaItemSerie } from '../../../../../shared/services/models/vendas/venda-item-serie.model';

/** As abas da seção (§6.1). A lista é a mesma; o que muda são as colunas. */
export type AbaItens = 'itens' | 'impostos';

interface OpcaoAba {
    key: AbaItens;
    label: string;
}

/**
 * Seção 3 — Itens (§6).
 *
 * As abas NÃO são componentes diferentes: são a mesma lista de itens vista por outro recorte de
 * colunas. Um FormArray só, e cada aba escolhe o que mostra.
 *
 * Impostos só existe com o cálculo de impostos ligado na configuração DO PROCESSO (§6.1). A
 * comissão fica só no modal do item.
 *
 * A seção é a dona do FormArray e também de qual linha está em edição na lista (`linhaEmEdicao`)
 * e de qual foi criada por "Adicionar item" e ainda não salva (`linhaNova`).
 */
@Component({
    selector: 'app-vd-itens',
    standalone: true,
    imports: [ReactiveFormsModule, MatButtonModule, MatIconModule, MatTooltipModule, VdItensListaComponent],
    templateUrl: './vd-itens.component.html',
    styleUrl: './vd-itens.component.css',
})
export class VdItensComponent implements OnDestroy {
    @Input({ required: true }) public set form(valor: FormGroup) {
        this.formAtual = valor;
        this.ligarPendentes();
        // Qualquer mudança nos itens (linha nova, removida, quantidade, preço) redistribui o desconto
        // global; o rateio grava com emitEvent falso, então não realimenta este fluxo.
        //
        // O shell recria esta seção a cada salvar e o FormArray é DELE: sem cancelar, as
        // assinaturas acumulavam e o rateio rodava N vezes por tecla depois de N salvamentos.
        //
        // Também marca o documento como SUJO: bipagem, kit, múltiplo e busca avançada escrevem na
        // lista por código (`push`/`setValue`), e isso não passa pela flag `dirty` do Angular — sem
        // esta linha, sair depois de bipar um item não perguntava "Sair sem salvar?". A carga do
        // documento não chega aqui (tudo com `emitEvent: false`).
        this.assinaturaDosItens?.unsubscribe();
        this.assinaturaDosItens = this.itens?.valueChanges.subscribe(() => {
            this.itens.markAsDirty();
            this.ratearDescontoGlobal();
        });
    }

    public get form(): FormGroup {
        return this.formAtual;
    }

    public ngOnDestroy(): void {
        this.assinaturaDosItens?.unsubscribe();
    }

    private formAtual!: FormGroup;
    private assinaturaDosItens: Subscription | undefined;
    @Input() public ehOrcamento: boolean = true;
    @Input() public calcularImpostos: boolean = false;
    @Input() public isSomenteLeitura: boolean = false;

    /** T03: documento gravado e usuário sem a permissão "Remover item" — remover item gravado pede liberação. */
    @Input() public exigeLiberacaoParaRemover: boolean = false;

    /** T03: a credencial do supervisor coletada ao remover um item gravado; o shell a usa no salvar. */
    @Output() public liberacaoRemocao = new EventEmitter<VdLiberacaoDialogResult>();

    /** §6.2: sem isto, os campos de preço de lista e desconto não existem no modal do item. */
    @Input() public exibeDescontoPorItem: boolean = false;

    /** §6.5: acima dele o salvamento vai exigir liberação. Nulo enquanto a regra não existe. */
    @Input() public tetoDesconto: number | null = null;

    /**
     * Fração do desconto GLOBAL do documento sobre os itens (0 a 1). Mexeu no desconto do
     * documento, a base da comissão de TODAS as linhas muda (é o líquido do rateio) — e o aviso
     * do teto de cada linha passa a olhar o desconto efetivo.
     */
    @Input() public set valorDescontoGlobal(valor: number | null) {
        const novo = UtilService.arredondarAbnt(Number(valor) || 0, 2);

        if (novo === this.descontoGlobalAtual) return;

        this.descontoGlobalAtual = novo;
        this.ratearDescontoGlobal();
    }

    public get valorDescontoGlobal(): number {
        return this.descontoGlobalAtual;
    }

    private descontoGlobalAtual: number = 0;

    /**
     * D21: escreve em cada linha a parcela do desconto global (`dados.valorDescontoRateado`, só de
     * leitura interna — nunca exibida) e refaz a comissão de quem mudou. Roda quando o desconto do
     * documento muda e quando qualquer item muda (total novo redistribui). A folha refaz a mesma
     * conta na gravação; a da tela existe para o aviso do teto e a comissão baterem com ela.
     */
    private ratearDescontoGlobal(): void {
        const linhas = (this.itens?.controls ?? []) as FormGroup[];

        if (!linhas.length) return;

        const parcelas = ratearDescontoGlobal(
            linhas.map((l) => Number(l.get('dados.valorTotal')?.value ?? 0) || 0),
            this.descontoGlobalAtual,
        );

        linhas.forEach((linha, i) => {
            if ((Number(linha.get('dados.valorDescontoRateado')?.value ?? 0) || 0) === parcelas[i]) return;

            linha.get('dados.valorDescontoRateado')?.setValue(parcelas[i], { emitEvent: false });
            recalcularValoresDoItem(linha);
        });
    }

    /** §6.2.2.3: Configurações ▸ Vendas ▸ Gerais ▸ "Priorizar menor preço entre listas". */
    @Input() public priorizarMenorPreco: boolean = true;

    /** "Permite estoque negativo" (Configurações ▸ Vendas ▸ Gerais) (§6.2.1). */
    @Input() public tipoValidacaoEstoqueNegativo: number | null = null;

    /** Documento já gravado: a reserva dele volta ao saldo consultado no lançamento (T21). */
    @Input() public idVenda: number | null = null;

    /**
     * Depósitos ativos da empresa. O depósito é do ITEM: a linha nova nasce com o padrão
     * (`isPadrao`) e o modal do item oferece a lista para trocar.
     */
    @Input() public localizacoesEstoque: LocalizacaoEstoque[] = [];

    private get idLocalizacaoEstoquePadrao(): number | null {
        return this.localizacoesEstoque.find((l) => l.isPadrao)?.idLocalizacaoEstoque ?? null;
    }

    /**
     * O catálogo de cores do kanban, que a tela já carregou nas configurações.
     *
     * Desce até aqui por causa do modal "Detalhes do estoque": a aba "últimas vendas" pinta a
     * etapa de cada documento, e a cor vem como ID de catálogo, nunca hexadecimal.
     */
    @Input() public coresKanban: KanbanCor[] = [];

    /**
     * A natureza de operação do DOCUMENTO. Item novo nasce com ela preenchida (§6.4).
     *
     * Item já lançado NÃO é recalculado quando ela muda: a natureza do item sobrepõe a do
     * documento naquela linha, e não há como distinguir "herdou" de "escolheu igual" — sair
     * reescrevendo apagaria a escolha de quem editou o item à mão.
     */
    @Input() public idNatOpCabecalho: number | null = null;

    /**
     * A lista do CABEÇALHO. Trocá-la re-resolve o preço dos itens já lançados (§4.4), menos os
     * que o usuário fixou à mão no popover — esses a §6.2.5 manda preservar.
     */
    @Input() public set idListaCabecalho(valor: number | null) {
        const anterior = this.idListaAtual;

        this.idListaAtual = valor;

        // Na PRIMEIRA atribuição não há troca: é o documento chegando. Recalcular ali sobrescreveria
        // o preço gravado de um documento que está sendo carregado.
        if (anterior !== undefined && anterior !== valor) this.reresolverPrecoDosItens();
    }

    public get idListaCabecalho(): number | null {
        return this.idListaAtual ?? null;
    }

    private idListaAtual: number | null | undefined = undefined;

    /**
     * As listas vigentes de cada produto, por idProduto — a matéria-prima da resolução de preço.
     *
     * Por PRODUTO e não por linha: duas linhas do mesmo produto têm as mesmas candidatas, e a
     * referência do mapa é estável, o que importa para quem o recebe por `@Input`.
     */
    public listasPorProduto = new Map<number, ProdutoListaPrecoVigenteStruct[]>();

    /** Quantos itens foram preservados na última troca de lista, para o aviso da §6.2.2.3. */
    private get contextoDePreco(): ContextoDePreco {
        return {
            idListaCabecalho: this.idListaCabecalho,
            priorizarMenorPreco: this.priorizarMenorPreco,
            // As casas vêm da máscara de preço, que a tela já montou a partir do usuário logado:
            // é a MESMA fonte que formata o campo, então o valor gravado e o exibido não divergem.
            casasDecimaisPreco: this.mascaraPreco?.precision ?? 2,
            casasDecimaisQuantidade: this.mascaraQuantidade?.precision ?? 2,
        };
    }

    /**
     * O vendedor do documento, para a comissão dos itens.
     *
     * Só muda quando o usuário troca o vendedor no cabeçalho — e aí TODOS os itens são
     * recalculados, porque a regra de comissão é do vendedor, não do item.
     */
    @Input() public set vendedor(valor: VendedorVendaStruct | null) {
        this.vendedorAtual = valor;
        this.itens?.controls.forEach((linha) => this.aplicarComissao(linha as FormGroup));
    }

    private vendedorAtual: VendedorVendaStruct | null = null;

    public aba: AbaItens = 'itens';

    /** Vem da tela: os totais e o pagamento usam as mesmas. */
    @Input() public mascaraPreco!: Partial<NgxCurrencyConfig>;
    @Input() public mascaraQuantidade!: Partial<NgxCurrencyConfig>;
    @Input() public mascaraInteira!: Partial<NgxCurrencyConfig>;
    @Input() public mascaraPercentual!: Partial<NgxCurrencyConfig>;

    constructor(
        private fb: FormBuilder,
        private dialog: MatDialog,
        private alertService: AlertService,
        private produtoLookupService: ProdutoLookupService,
        private elementRef: ElementRef<HTMLElement>,
        private injector: Injector,
    ) {}

    /**
     * Cópia de documento (Clonar, task 148656): ao carregar, os itens são REPRECIFICADOS pela lista
     * vigente (decisão do Pedro, 26/09). O documento novo vale pelo preço de hoje — e é o que o
     * servidor confere para item novo; manter o preço da origem fazia a cópia ser recusada para
     * quem não tem o perfil de preço.
     */
    @Input() public reprecificarAoCarregar: boolean = false;

    /** "Recalcular preços" do cabeçalho (§4.1): o contador muda a cada clique, e a mudança é o sinal. */
    @Input() public set pedidoRecalcularPrecos(valor: number) {
        if (valor > 0) this.reprecificarPelaListaVigente(true);
    }

    public get itens(): FormArray {
        return this.form.get('itens') as FormArray;
    }

    public get temItens(): boolean {
        return this.itens.length > 0;
    }

    public get abas(): OpcaoAba[] {
        const lista: OpcaoAba[] = [
            { key: 'itens', label: 'Produtos ou serviços' },
        ];

        if (this.calcularImpostos) lista.push({ key: 'impostos', label: 'Impostos' });

        return lista;
    }

    /** A aba escolhida, ou a de produtos quando ela deixou de existir (impostos desligados). */
    public get abaAtiva(): AbaItens {
        return this.abas.some((a) => a.key === this.aba) ? this.aba : 'itens';
    }

    // ==================================================================== edição na lista

    /** A linha que mostra campos na lista. Uma por vez. */
    public linhaEmEdicao: FormGroup | null = null;

    /** A linha criada por "Adicionar item" e ainda não salva: Descartar a remove. */
    public linhaNova: FormGroup | null = null;

    /**
     * Item novo entra VAZIO e em edição, e é preenchido na própria linha: quem está lançando rápido
     * digita e segue, sem abrir diálogo nenhum. A busca avançada existe para o outro caso: quando
     * o usuário não sabe o que procura.
     *
     * Com uma linha nova ainda sem produto, não cria outra: a vazia já é onde digitar.
     */
    public adicionar(evento: MouseEvent): void {
        // O clique não chega ao documento: a lista o trataria como "clique fora" da linha nova.
        evento.stopPropagation();

        if (this.linhaNova && !this.temProduto(this.linhaNova)) return;

        this.encerrarEdicao();

        const linha = this.criarLinha();

        this.itens.push(linha);
        this.linhaEmEdicao = linha;
        this.linhaNova = linha;

        // A linha nova entra no fim da lista: a tela rola até ela. Tabela e cards coexistem no DOM; vale a visível.
        afterNextRender(() => {
            const linhaNova = Array.from(this.elementRef.nativeElement.querySelectorAll<HTMLElement>('.linha-em-edicao'))
                .find((e) => e.offsetParent !== null);

            linhaNova?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, { injector: this.injector });
    }

    /** Clique numa linha em texto: ela passa a ser a linha em edição. */
    public editarNaLinha(linha: FormGroup): void {
        this.encerrarEdicao();

        // A linha nova vazia não tem o que guardar: sai da lista quando o usuário vai editar outra.
        if (this.linhaNova && !this.temProduto(this.linhaNova)) this.descartarLinha();

        this.linhaEmEdicao = linha;
    }

    /** Clique fora: a linha volta para texto, se for válida (com produto). */
    public sairDaEdicao(): void {
        this.encerrarEdicao();
    }

    /**
     * "Salvar" da linha: ela fica na lista, em texto. Lote e série são escolhidos no modal do item ou no lançar estoque (D5);
     * aqui só a quantidade reduzida abaixo do que já foi selecionado é barrada, porque o servidor recusa.
     */
    public salvarLinha(): void {
        const excesso = this.linhaEmEdicao ? motivoDeExcessoDeLoteSerie(this.linhaEmEdicao, this.casasDecimaisQuantidade) : null;
        if (excesso) {
            this.alertService.show('Aviso', excesso, AlertType.warning);
            return;
        }
        this.encerrarEdicao();
    }

    /** "Descartar" da linha nova: ela sai da lista. */
    public descartarLinha(): void {
        const linha = this.linhaNova;

        this.linhaNova = null;
        if (this.linhaEmEdicao === linha) this.linhaEmEdicao = null;

        const indice = linha ? this.itens.controls.indexOf(linha) : -1;

        if (indice >= 0) this.remover(indice);
    }

    /** Devolve a linha em edição para texto quando ela tem produto; sem produto, ela continua em edição. */
    private encerrarEdicao(): void {
        const linha = this.linhaEmEdicao;

        if (!linha || !this.temProduto(linha)) return;

        const ehNova = this.linhaNova === linha;

        if (ehNova) this.linhaNova = null;

        this.linhaEmEdicao = null;
    }

    private temProduto(linha: FormGroup): boolean {
        return !!linha.get('dados.idProduto')?.value;
    }

    /**
     * T03: item já gravado (`dados.idVendaItem`) só sai com a liberação de quem tem a permissão
     * "Remover item" — coletada aqui, no clique, e conferida pelo orquestrador no salvar. Item novo
     * sai livre. O diálogo de liberação é a própria confirmação; cancelado, a linha fica.
     */
    public remover(indice: number): void {
        const linha = this.itens.at(indice) as FormGroup;

        if (!this.exigeLiberacaoParaRemover || !(Number(linha.get('dados.idVendaItem')?.value) > 0)) {
            this.removerDaLista(indice);
            return;
        }

        const quantidade = Number(linha.get('dados.quantidade')?.value) || 0;
        const descricao = `${linha.get('dados.descricao')?.value || 'item'} × ${quantidade.toLocaleString('pt-BR', { maximumFractionDigits: 4 })}`;

        this.abrirLiberacaoDeRemocao([descricao]).afterClosed().subscribe((credencial) => {
            if (!credencial) return;

            this.liberacaoRemocao.emit(credencial);
            this.removerDaLista(this.itens.controls.indexOf(linha));
        });
    }

    private removerDaLista(indice: number): void {
        const linha = this.itens.at(indice);

        if (linha === this.linhaEmEdicao) this.linhaEmEdicao = null;
        if (linha === this.linhaNova) this.linhaNova = null;

        this.itens.removeAt(indice);
        this.renumerar();
    }

    /** O mesmo diálogo do salvar (vd-liberacao-dialog), só com o motivo da remoção. */
    private abrirLiberacaoDeRemocao(removidos: string[]): MatDialogRef<VdLiberacaoDialogComponent, VdLiberacaoDialogResult> {
        const data: VdLiberacaoDialogData = { bloqueios: [bloqueioDeRemocao(removidos)] };

        return this.dialog.open<VdLiberacaoDialogComponent, VdLiberacaoDialogData, VdLiberacaoDialogResult>(VdLiberacaoDialogComponent, {
            data,
            width: '640px',
            maxWidth: '96vw',
            autoFocus: false,
            disableClose: true,
        });
    }

    /**
     * Busca avançada (§6.2.2) e bipagem (§6.2.1) são o MESMO diálogo: o campo Pesquisar recebe o
     * leitor de código de barras, código exato volta como `lidoPorCodigo` e entra pelas regras da
     * leitura (soma à linha, múltiplo, série); escolha na lista entra como item comum. Depois de uma
     * leitura o diálogo reabre sozinho — quem bipa, bipa vários; fecha quando terminar.
     */
    public abrirBuscaAvancada(): void {
        // O item lançado pela busca nasce no depósito padrão: é nele que o saldo é conferido.
        const data: VdProdutoBuscaDialogData = {
            idLocalizacaoEstoqueParaSaldo: localizacaoEstoqueParaConsultaDeSaldo(this.tipoValidacaoEstoqueNegativo, this.idLocalizacaoEstoquePadrao),
            idVendaReserva: this.idVenda,
            idListaCabecalho: this.idListaCabecalho,
            priorizarMenorPreco: this.priorizarMenorPreco,
        };

        this.dialog.open(VdProdutoBuscaDialogComponent, { data, width: '980px', maxWidth: '96vw', autoFocus: '[cdkFocusInitial]' })
            .afterClosed()
            .subscribe((escolha: ProdutoEscolhido | undefined) => {
                if (!escolha) return;

                if (escolha.lidoPorCodigo) {
                    this.lancarLeitura(escolha.produto, escolha.codigoComposto ?? null);
                    this.abrirBuscaAvancada();
                    return;
                }

                if (escolha.produto.isKit) {
                    this.incluirKit(escolha.produto, null);
                    return;
                }

                const regra = regraDoMultiplo(escolha.produto);

                // Saldo no lançamento (§6.2.1), com a quantidade de UM item; com múltiplo a
                // quantidade sai do diálogo, e a conferência acontece lá.
                if (!regra) {
                    const saldo = validarSaldoNoLancamento(this.tipoValidacaoEstoqueNegativo, escolha.produto.saldoDisponivel, 1,
                        escolha.produto.descricao, this.casasDecimaisQuantidade);

                    if (!saldo.permitido) {
                        this.alertService.show('Estoque insuficiente', saldo.aviso ?? '', AlertType.warning);
                        return;
                    }

                    if (saldo.aviso) this.alertService.show('Saldo insuficiente', saldo.aviso, AlertType.info);
                }

                const linha = this.criarLinha();

                this.registrarListasDoProduto(escolha.produto);
                preencherDoProduto(linha, escolha.produto, this.contextoDePreco);
                linha.get('dados.descricaoComplementar')?.setValue(escolha.descricaoComplementar);
                this.itens.push(linha);

                if (!regra) return;

                this.abrirMultiplo(this.itens.length - 1, {
                    descricao: escolha.produto.descricao,
                    codigoSku: escolha.produto.codigoSku ?? null,
                    unidade: escolha.produto.unidade ?? null,
                    regra,
                    quantidadeAtual: null,
                    saldoDisponivel: escolha.produto.saldoDisponivel ?? null,
                    ehLancamento: true,
                    removerAoCancelar: true,
                });
            });
    }

    // ==================================================================== kit (§6.9)

    /**
     * Produto-kit escolhido no campo Item da linha `indice`: abre "Incluir kit" e, confirmado,
     * a linha onde o kit foi escolhido dá lugar aos componentes — na MESMA posição.
     */
    public aoEscolherKit(evento: { indice: number; produto: ProdutoListStruct }): void {
        this.incluirKit(evento.produto, evento.indice);
    }

    // ==================================================================== múltiplo (§6.2.3)

    /** Produto com múltiplo escolhido no campo Item: a linha já está preenchida, falta a quantidade. */
    public aoMultiploEscolhido(evento: { indice: number; produto: ProdutoListStruct }): void {
        const regra = regraDoMultiplo(evento.produto);

        if (!regra) return;

        this.abrirMultiplo(evento.indice, {
            descricao: evento.produto.descricao,
            codigoSku: evento.produto.codigoSku ?? null,
            unidade: evento.produto.unidade ?? null,
            regra,
            quantidadeAtual: null,
            saldoDisponivel: evento.produto.saldoDisponivel ?? null,
            ehLancamento: true,
        });
    }

    /** Qtde clicada num item com múltiplo: reabre o diálogo com a quantidade atual (§6.2.3). */
    public editarMultiplo(indice: number): void {
        const linha = this.itens.at(indice) as FormGroup;
        const regra = regraDoMultiplo({
            isControlaMultiplo: linha.get('multiplo.isControlaMultiplo')?.value,
            fatorMultiplo: linha.get('multiplo.fatorMultiplo')?.value,
            unidadeMultiplo: linha.get('multiplo.unidadeMultiplo')?.value,
        });

        if (!regra) return;

        this.abrirMultiplo(indice, {
            descricao: linha.get('dados.descricao')?.value ?? '',
            codigoSku: linha.get('dados.codigoSku')?.value ?? null,
            unidade: linha.get('dados.siglaUnidade')?.value ?? null,
            regra,
            quantidadeAtual: Number(linha.get('dados.quantidade')?.value ?? 0),
            saldoDisponivel: null,
            ehLancamento: false,
        });
    }

    private abrirMultiplo(indice: number, opcoes: {
        descricao: string;
        codigoSku: string | null;
        unidade: string | null;
        regra: RegraDoMultiplo;
        quantidadeAtual: number | null;
        saldoDisponivel: number | null;
        ehLancamento: boolean;
        /** A linha foi criada para este lançamento (busca avançada): cancelar a remove, em vez de deixá-la vazia. */
        removerAoCancelar?: boolean;
    }): void {
        // A linha já foi preenchida do produto (lançamento) ou é a da lista (edição): a unidade vem dela.
        const isUnidadeMedidaFracionada = this.itens.at(indice)?.get('dados.isUnidadeMedidaFracionada')?.value !== false;
        const data: VdMultiploDialogData = {
            descricao: opcoes.descricao,
            codigoSku: opcoes.codigoSku,
            unidade: opcoes.unidade,
            regra: opcoes.regra,
            quantidadeAtual: opcoes.quantidadeAtual,
            isUnidadeMedidaFracionada,
            casasDecimaisQuantidade: this.casasDecimaisQuantidade,
            mascaraQuantidade: isUnidadeMedidaFracionada ? this.mascaraQuantidade : this.mascaraInteira,
        };

        this.dialog.open(VdMultiploDialogComponent, { data, width: '680px', maxWidth: '96vw', autoFocus: '[cdkFocusInitial]' })
            .afterClosed()
            .subscribe((quantidade: number | undefined) => {
                // Cancelar no lançamento não lança nada (§6.2.3): a linha volta a vazia — ou some,
                // quando foi criada só para este lançamento.
                if (quantidade == null) {
                    if (opcoes.ehLancamento) this.desfazerLancamento(indice, opcoes.removerAoCancelar === true);

                    return;
                }

                if (opcoes.ehLancamento) {
                    const saldo = validarSaldoNoLancamento(this.tipoValidacaoEstoqueNegativo, opcoes.saldoDisponivel,
                        quantidade, opcoes.descricao, this.casasDecimaisQuantidade);

                    if (!saldo.permitido) {
                        this.alertService.show('Estoque insuficiente', saldo.aviso ?? '', AlertType.warning);
                        this.desfazerLancamento(indice, opcoes.removerAoCancelar === true);

                        return;
                    }

                    if (saldo.aviso) this.alertService.show('Saldo insuficiente', saldo.aviso, AlertType.info);
                }

                const linha = this.itens.at(indice) as FormGroup;

                linha.get('dados.quantidade')?.setValue(quantidade);
            });
    }

    private desfazerLancamento(indice: number, remover: boolean): void {
        if (remover) {
            this.itens.removeAt(indice);
            this.renumerar();
            return;
        }

        this.limparLinha(indice);
    }

    /** Lançamento cancelado ou recusado: a linha volta a vazia, na mesma posição e sequência. */
    private limparLinha(indice: number): void {
        const anterior = this.itens.at(indice);
        const sequencia = anterior?.get('dados.sequencia')?.value ?? indice + 1;
        const nova = this.criarLinha();

        nova.get('dados.sequencia')?.setValue(sequencia, { emitEvent: false });
        this.itens.setControl(indice, nova);

        // A linha nova continua em edição, agora vazia.
        if (anterior === this.linhaEmEdicao) this.linhaEmEdicao = nova;
        if (anterior === this.linhaNova) this.linhaNova = nova;
    }

    // ==================================================================== leitura de código (§6.2.1)

    /**
     * Lança o que uma LEITURA de código devolveu (§6.2.1): kit abre o diálogo do kit; múltiplo soma
     * UMA unidade de múltiplo (§6.2.3); produto já na lista soma à linha existente. Lote e série lidos
     * entram nas listas do item (`lotes`, `series`), como no item da NF-e de saída: o lote lido soma a
     * quantidade lida ao lote (ou entra novo); a série lida soma uma unidade e entra na lista, e a mesma
     * série lida de novo é recusada. O saldo é conferido com a quantidade FINAL.
     */
    private lancarLeitura(produto: ProdutoListStruct, composto: CodigoCompostoResolvidoStruct | null): void {
        if (produto.isKit) {
            this.incluirKit(produto, null);
            return;
        }

        const regra = regraDoMultiplo(produto);
        const loteLido = composto?.tipoIdentificador === TipoIdentificadorCodigoCompostoProdutoEnum.Lote ? composto : null;
        const serieLida = composto?.tipoIdentificador === TipoIdentificadorCodigoCompostoProdutoEnum.Serie ? composto : null;
        const existente = this.linhaDoProduto(produto.idProduto);

        if (serieLida && existente && this.seriesDaLinha(existente).some((s) => s.idSerie === serieLida.idIdentificador)) {
            this.alertService.show('Série já lançada', `${produto.descricao}: a série ${serieLida.codigo ?? ''} já está no item.`, AlertType.warning);
            return;
        }

        const quantidadeLida = serieLida ? 1 : (regra ? regra.fatorMultiplo : 1);
        const quantidadeFinal = (existente ? Number(existente.get('dados.quantidade')?.value ?? 0) : 0) + quantidadeLida;

        const saldo = validarSaldoNoLancamento(this.tipoValidacaoEstoqueNegativo, produto.saldoDisponivel,
            quantidadeFinal, produto.descricao, this.casasDecimaisQuantidade);

        if (!saldo.permitido) {
            this.alertService.show('Estoque insuficiente', saldo.aviso ?? '', AlertType.warning);
            return;
        }

        if (saldo.aviso) this.alertService.show('Saldo insuficiente', saldo.aviso, AlertType.info);

        let linha = existente;

        if (linha) {
            linha.get('dados.quantidade')?.setValue(quantidadeFinal);
        } else {
            linha = this.criarLinha();

            this.registrarListasDoProduto(produto);
            preencherDoProduto(linha, produto, this.contextoDePreco);
            linha.get('dados')?.patchValue({ quantidade: quantidadeLida });
            this.itens.push(linha);
        }

        if (loteLido) {
            const lotes = (linha.get('lotes')?.value as VendaItemRastreabilidade[] | null) ?? [];
            const doLote = lotes.find((l) => l.idLote === loteLido.idIdentificador);
            const novos: VendaItemRastreabilidade[] = doLote
                ? lotes.map((l) => (l === doLote ? { ...l, quantidadeLote: Number(l.quantidadeLote ?? 0) + quantidadeLida } : l))
                : [...lotes, {
                    idLote: loteLido.idIdentificador,
                    numeroLote: loteLido.codigo ?? null,
                    quantidadeLote: quantidadeLida,
                    dataFabricacao: null,
                    dataValidade: null,
                    codigoAgregacao: null,
                }];

            linha.get('lotes')?.setValue(novos);
        }

        if (serieLida) {
            const serie: VendaItemSerie = { idSerie: serieLida.idIdentificador, numeroSerie: serieLida.codigo ?? null };

            linha.get('series')?.setValue([...this.seriesDaLinha(linha), serie]);
        }

        this.alertService.show('Item adicionado', `${produto.descricao}: ${existente ? 'quantidade somada à linha existente' : 'lançado'}.`, AlertType.success);
    }

    /** A linha avulsa (não componente de kit) do produto: lotes e séries ficam nas listas do item. */
    private linhaDoProduto(idProduto: number): FormGroup | null {
        return (this.itens.controls as FormGroup[]).find((l) =>
            Number(l.get('dados.idProduto')?.value) === idProduto
            && l.get('kit.numeroKit')?.value == null) ?? null;
    }

    private seriesDaLinha(linha: FormGroup): VendaItemSerie[] {
        return (linha.get('series')?.value as VendaItemSerie[] | null) ?? [];
    }

    private get casasDecimaisQuantidade(): number {
        return this.mascaraQuantidade?.precision ?? 2;
    }

    private incluirKit(produto: ProdutoListStruct, indiceDaLinhaVazia: number | null): void {
        const data: VdKitDialogData = {
            produto,
            contexto: this.contextoDePreco,
            exibeDescontoPorItem: this.exibeDescontoPorItem,
            tetoDesconto: this.tetoDesconto,
            mascaraPreco: this.mascaraPreco,
            mascaraQuantidade: this.mascaraQuantidade,
            mascaraInteira: this.mascaraInteira,
            mascaraPercentual: this.mascaraPercentual,
        };

        this.dialog.open(VdKitDialogComponent, { data, width: '860px', maxWidth: '96vw', autoFocus: false })
            .afterClosed()
            .subscribe((plano: PlanoDoKit | undefined) => {
                if (indiceDaLinhaVazia != null) this.desfazerEscolhaNaLinha(indiceDaLinhaVazia);

                if (!plano) return;

                this.lancarKit(plano, indiceDaLinhaVazia);
            });
    }

    /** O select da linha ficou apontando para o kit; cancelado ou confirmado, a linha não é o kit. */
    private desfazerEscolhaNaLinha(indice: number): void {
        const linha = this.itens.at(indice) as FormGroup | undefined;

        linha?.get('dados.idProduto')?.setValue(null, { emitEvent: false });
    }

    /**
     * O plano vira N linhas de componente com o mesmo `numeroKit`, inseridas onde a linha vazia
     * estava (ou no fim). A linha vazia sai: o kit não é item.
     */
    private lancarKit(plano: PlanoDoKit, indiceDaLinhaVazia: number | null): void {
        const numeroKit = proximoNumeroKit(this.itens);
        let posicao = indiceDaLinhaVazia ?? this.itens.length;

        if (indiceDaLinhaVazia != null) {
            const vazia = this.itens.at(indiceDaLinhaVazia);

            // A linha nova onde o kit foi escolhido dá lugar aos componentes.
            if (vazia === this.linhaEmEdicao) this.linhaEmEdicao = null;
            if (vazia === this.linhaNova) this.linhaNova = null;

            this.itens.removeAt(indiceDaLinhaVazia, { emitEvent: false });
        }

        for (const componente of plano.componentes) {
            const linha = montarFormItemVenda(this.fb);

            linha.get('dados.idNatOp')?.setValue(this.idNatOpCabecalho, { emitEvent: false });
            linha.get('dados.idLocalizacaoEstoque')?.setValue(this.idLocalizacaoEstoquePadrao, { emitEvent: false });
            this.registrarListasDoProduto(componente.produto);
            aplicarComponenteNaLinha(linha, plano, componente, numeroKit);
            this.ligarRecalculo(linha);

            this.itens.insert(posicao++, linha);
        }

        this.renumerar();
    }

    /** Aviso vindo da lista: kit fixo que não fechou no preço. */
    public avisar(texto: string): void {
        this.alertService.show('Kit não fecha no preço', texto, AlertType.warning);
    }

    public excluirKit(numeroKit: number): void {
        const linhas = linhasDoKit(this.itens, numeroKit);

        if (!linhas.length) return;

        const nome = linhas[0].get('kit.descricaoKit')?.value || 'kit';

        this.dialog
            .open(ConfirmModalComponent, {
                data: {
                    title: 'Excluir o kit inteiro?',
                    description: `Os componentes de "${nome}" são lançados e removidos juntos. Deseja excluir o kit e os seus ${linhas.length} componentes?`,
                    buttonConfirm: 'Excluir kit',
                    buttonClose: 'Cancelar',
                },
            })
            .afterClosed()
            .subscribe((result) => {
                if (!result?.confirm) return;

                // T03: kit já gravado pede a liberação uma vez só, para todos os componentes.
                const temGravado = linhas.some((linha) => Number(linha.get('dados.idVendaItem')?.value) > 0);

                if (!this.exigeLiberacaoParaRemover || !temGravado) {
                    this.removerLinhasDoKit(linhas);
                    return;
                }

                this.abrirLiberacaoDeRemocao([`Kit ${nome} — ${linhas.length} componentes`]).afterClosed().subscribe((credencial) => {
                    if (!credencial) return;

                    this.liberacaoRemocao.emit(credencial);
                    this.removerLinhasDoKit(linhas);
                });
            });
    }

    private removerLinhasDoKit(linhas: FormGroup[]): void {
        for (const linha of linhas) {
            const indice = this.itens.controls.indexOf(linha);

            if (indice >= 0) this.itens.removeAt(indice, { emitEvent: false });
        }

        this.itens.updateValueAndValidity();
        this.renumerar();
    }

    /**
     * Guarda as listas vigentes que vieram com o produto.
     *
     * Chamado pelos DOIS caminhos de lançamento — o campo Item e a busca avançada —, porque o
     * rastreio do preço e o popover leem daqui, e um produto lançado por um caminho não pode
     * ficar sem o que o outro tem.
     */
    public registrarListasDoProduto(produto: ProdutoListStruct): void {
        if (!produto?.idProduto) return;

        this.listasPorProduto.set(produto.idProduto, produto.listasPreco ?? []);
    }

    /**
     * Trocar a lista do cabeçalho re-resolve o preço item a item (§4.4 e §6.2.2.3).
     *
     * Item com lista escolhida à mão NÃO é tocado: a escolha manual prevalece sobre a regra
     * automática, e o usuário é avisado de quantos ficaram como estavam.
     *
     * Item cujo produto não tem candidatas conhecidas também fica quieto — é o caso do documento
     * carregado do servidor, onde os itens chegam gravados e ninguém consultou o produto ainda.
     * Recalcular ali seria trocar um preço gravado por um chute. Decisão D24 (Pedro, 25/09): sem
     * consulta nova só por causa da troca — no máximo o AVISO de quantos ficaram com o preço
     * gravado; quem quiser reprecificar reabre o produto na linha.
     */
    private reresolverPrecoDosItens(): void {
        let preservados = 0;
        let recalculados = 0;
        let gravados = 0;

        this.itens.controls.forEach((controle) => {
            const linha = controle as FormGroup;
            const idProduto = linha.get('dados.idProduto')?.value;

            if (!idProduto) return;

            if (linha.get('dados.isListaPrecoManual')?.value) {
                preservados++;
                return;
            }

            const candidatas = this.listasPorProduto.get(idProduto);

            if (!candidatas?.length) {
                gravados++;
                return;
            }

            aplicarListaNoItem(linha, null, this.contextoDePreco, candidatas);
            recalculados++;
        });

        if (recalculados || preservados || gravados) this.avisarTrocaDeLista(recalculados, preservados, gravados);
    }

    private avisarTrocaDeLista(recalculados: number, preservados: number, gravados: number): void {
        const partes = [`${recalculados} ${recalculados === 1 ? 'item recalculado' : 'itens recalculados'}`];

        if (preservados) {
            partes.push(`${preservados} ${preservados === 1 ? 'mantido' : 'mantidos'} por escolha manual de lista`);
        }

        if (gravados) {
            partes.push(`${gravados} ${gravados === 1 ? 'mantido' : 'mantidos'} com o preço gravado — reabra o produto na linha para reprecificar`);
        }

        // Só itens gravados intocados não é sucesso de nada: é um aviso de que o preço não mudou.
        this.alertService.show('Lista de preço alterada', partes.join(' · '), recalculados ? AlertType.success : AlertType.info);
    }

    /**
     * "Detalhes do estoque" da linha (§6.3).
     *
     * O diálogo é de LEITURA e não devolve nada — por isso não há `afterClosed`. O cliente vem
     * do CABEÇALHO do documento (`idPessoaCliente` na raiz do form), e não da linha: a aba
     * "últimas vendas" pergunta o que este cliente já comprou deste produto.
     */
    public detalhesEstoque(indice: number): void {
        const linha = this.itens.at(indice) as FormGroup;
        const dados = linha.get('dados');
        const idProduto = dados?.get('idProduto')?.value;

        // A linha sem produto nem renderiza o botão (§6.3); a guarda é para a chamada por código.
        if (!idProduto) return;

        const data: VdEstoqueDialogData = {
            idProduto,
            descricao: dados?.get('descricao')?.value ?? '',
            codigoSku: dados?.get('codigoSku')?.value ?? null,
            codigoNcm: dados?.get('codigoNcm')?.value ?? null,
            siglaUnidade: dados?.get('siglaUnidade')?.value ?? null,
            quantidade: Number(dados?.get('quantidade')?.value ?? 0),
            idPessoaCliente: this.form.get('idPessoaCliente')?.value ?? null,
            coresKanban: this.coresKanban,
        };

        this.dialog.open(VdEstoqueDialogComponent, { data, width: '820px', maxWidth: '96vw', autoFocus: false });
    }

    /** O modal edita uma CÓPIA e só devolve no "Confirmar": por isso a linha vai como está. */
    public editar(indice: number): void {
        this.abrirModalDoItem(indice);
    }

    private abrirModalDoItem(indice: number): void {
        const linha = this.itens.at(indice) as FormGroup;

        const data: VdItemDialogData = {
            linha,
            ehOrcamento: this.ehOrcamento,
            calcularImpostos: this.calcularImpostos,
            isSomenteLeitura: this.isSomenteLeitura,
            exibeDescontoPorItem: this.exibeDescontoPorItem,
            tetoDesconto: this.tetoDesconto,
            idNatOpDocumento: this.idNatOpCabecalho,
            localizacoesEstoque: this.localizacoesEstoque,
            idVenda: this.idVenda,
            mascaraPreco: this.mascaraPreco,
            mascaraQuantidade: this.mascaraQuantidade,
            mascaraInteira: this.mascaraInteira,
            mascaraPercentual: this.mascaraPercentual,
        };

        this.dialog.open<VdItemDialogComponent, VdItemDialogData, boolean>(VdItemDialogComponent,
            { data, width: '940px', maxWidth: '96vw', autoFocus: false });
    }

    /**
     * Itens de um documento GRAVADO (task 148606). As linhas já estão no FormArray: quem as monta é
     * a página, no `subscribe` do carregamento (`montarItens`), antes da detecção de mudanças —
     * montá-las aqui, no setter, acontecia depois de a página já ter lido o FormArray para o badge
     * do título (NG0100). Aqui só se liga o recálculo em cada linha e se fecham a sequência e o
     * rateio do desconto.
     *
     * `recalcularAgora: false` na ligação: item carregado mostra o que está GRAVADO. Reapurar na
     * abertura trocaria o snapshot do documento por uma prévia — e no kit de preço fixo chegaria
     * a mexer no `valorTotal`, que por decisão de modelagem não é `quantidade × precoLiquidoUnitario`.
     * A partir da primeira edição o recálculo volta a valer, como em qualquer linha.
     */
    @Input() public set itensDoDocumento(itens: Record<string, unknown>[] | null | undefined) {
        // Só o documento CARREGADO passa por aqui. Nulo é "ainda não chegou".
        if (!itens) return;

        this.itensPendentes = itens;
        this.ligarPendentes();
    }

    private itensPendentes: Record<string, unknown>[] | null = null;

    /**
     * Liga as linhas assim que o FORM também estiver disponível.
     *
     * O Angular atribui os `@Input` na ordem em que aparecem no template, e hoje `[form]` vem
     * antes de `[itensDoDocumento]` — então na prática o form já chegou. Mas isso é uma garantia
     * de ORDEM NO TEMPLATE, não do contrato do componente: alguém reordenando os bindings
     * quebraria o carregamento com um erro obscuro de `this.itens` indefinido. A espera explícita
     * custa três linhas e torna o componente indiferente à ordem.
     */
    private ligarPendentes(): void {
        if (!this.form || !this.itensPendentes) return;

        this.itensPendentes = null;

        for (const linha of this.itens.controls) {
            this.ligarRecalculo(linha as FormGroup, false);
        }

        // §6.2.4: a sequência é do documento e vale para a impressão. As vendas anteriores à
        // coluna têm TODAS as linhas em zero — e aí a ordem seria indeterminada —, então renumerar
        // ao abrir é o que dá ordem a elas sem exigir que alguém arraste linha por linha.
        this.renumerar();
        // O rateio roda UMA vez, com todas as linhas (`push` com evento o disparava pela metade).
        this.ratearDescontoGlobal();

        if (this.reprecificarAoCarregar) this.reprecificarPelaListaVigente();
    }

    /**
     * Consulta as listas vigentes dos produtos das linhas e re-resolve o preço de cada uma com a
     * mesma regra do lançamento (`aplicarListaNoItem`): lista escolhida à mão continua a mesma, só
     * com o preço de hoje, e o % negociado é mantido. Componente de kit de preço FIXO fica de fora: o preço dele é rateio do kit, e o
     * servidor também não o confere. Roda ao abrir a cópia e no botão "Recalcular preços" do cabeçalho (`avisarSemMudanca`:
     * quem clicou quer uma resposta mesmo quando nada muda).
     */
    private reprecificarPelaListaVigente(avisarSemMudanca: boolean = false): void {
        const linhas = (this.itens.controls as FormGroup[]).filter((linha) =>
            linha.get('dados.idProduto')?.value
            && !(linha.get('kit.numeroKit')?.value != null && linha.get('kit.isPrecoFixoKit')?.value === true));

        const ids = [...new Set(linhas.map((linha) => Number(linha.get('dados.idProduto')?.value)))];

        if (!ids.length) {
            if (avisarSemMudanca) this.alertService.show('Sem itens', 'Não há itens com produto para recalcular.', AlertType.info);
            return;
        }

        this.produtoLookupService.getAllSelectByIds(ids, true).subscribe({
            next: (res) => {
                if (res.isError) {
                    this.alertService.show('Atenção', 'Não foi possível consultar os preços atuais dos itens.', AlertType.warning);
                    return;
                }

                (res.list ?? []).forEach((produto) => this.registrarListasDoProduto(produto));

                let alterados = 0;

                for (const linha of linhas) {
                    const antes = Number(linha.get('dados.precoLiquidoUnitario')?.value ?? 0);
                    const idListaManual = linha.get('dados.isListaPrecoManual')?.value
                        ? linha.get('dados.idListaPreco')?.value
                        : null;

                    aplicarListaNoItem(
                        linha,
                        idListaManual ? { idListaPreco: idListaManual, preco: 0 } : null,
                        this.contextoDePreco,
                        this.listasPorProduto.get(Number(linha.get('dados.idProduto')?.value)) ?? [],
                    );

                    if (Number(linha.get('dados.precoLiquidoUnitario')?.value ?? 0) !== antes) alterados++;
                }

                this.ratearDescontoGlobal();

                if (alterados) {
                    this.alertService.show(
                        'Preços atualizados',
                        `${alterados} ${alterados === 1 ? 'item teve o preço atualizado' : 'itens tiveram o preço atualizado'} pela lista vigente.`,
                        AlertType.info,
                    );
                } else if (avisarSemMudanca) {
                    this.alertService.show('Preços conferidos', 'Os itens já estão com o preço da lista vigente.', AlertType.info);
                }
            },
            error: () => this.alertService.show('Atenção', 'Não foi possível consultar os preços atuais dos itens.', AlertType.warning),
        });
    }

    private criarLinha(): FormGroup {
        const linha = montarFormItemVenda(this.fb);

        linha.get('dados.sequencia')?.setValue(this.itens.length + 1);

        // Nasce com a natureza do documento já escolhida (§6.4). Deixá-la vazia obrigava o usuário
        // a abrir o modal e repetir o que ele acabou de informar no cabeçalho — e o protótipo
        // mostra a do documento no campo do item exatamente por isso.
        linha.get('dados.idNatOp')?.setValue(this.idNatOpCabecalho);

        // O depósito é do item e nasce no padrão da empresa; troca-se no modal do item.
        linha.get('dados.idLocalizacaoEstoque')?.setValue(this.idLocalizacaoEstoquePadrao);

        this.ligarRecalculo(linha);

        return linha;
    }

    /**
     * Os valores apurados do item acompanham o que se edita — na LINHA, e não só no modal.
     *
     * Quantidade, preço, desconto, alíquotas e percentual de comissão mexem todos na mesma base,
     * e quem digita na lista espera ver o efeito ali mesmo. Sem isto, as colunas da aba Impostos
     * ficavam zeradas até alguém abrir o modal do item.
     *
     * Não há laço: `recalcularValoresDoItem` escreve os campos de valor com `emitEvent: false`.
     */
    private ligarRecalculo(linha: FormGroup, recalcularAgora: boolean = true): void {
        linha.valueChanges.subscribe(() => recalcularValoresDoItem(linha));

        // A comissão por faixa segue o desconto efetivo do item contra o preço bruto
        // (`percentualDaComissao`): muda com o desconto e com o preço.
        const desconto = linha.get('dados.descontoPercentual');
        const preco = linha.get('dados.precoLiquidoUnitario');

        if (desconto && preco) {
            merge(desconto.valueChanges, preco.valueChanges).subscribe(() => this.aplicarComissao(linha));
        }

        // Kit por SOMA: o preço do kit é derivado dos componentes, então editar um componente
        // atualiza o `precoKit` do grupo inteiro (o cabeçalho e o request leem de lá).
        linha.get('dados.precoLiquidoUnitario')?.valueChanges.subscribe(() => {
            const numeroKit = linha.get('kit.numeroKit')?.value;

            if (numeroKit == null || linha.get('kit.isPrecoFixoKit')?.value === true) return;

            sincronizarPrecoKitPorSoma(linhasDoKit(this.itens, Number(numeroKit)), this.mascaraPreco?.precision ?? 2);
        });

        // Linha NOVA nasce zerada e precisa da primeira apuracao; linha CARREGADA ja vem com os
        // valores gravados, e apurar por cima deles apagaria o snapshot do documento.
        if (!recalcularAgora) return;

        recalcularValoresDoItem(linha);
        this.aplicarComissao(linha);
    }

    /**
     * Escreve no item o percentual de comissao que o cadastro do vendedor determina.
     *
     * `null` do calculo significa "o cadastro nao decide isto" — vendedor nao informado, sem tipo
     * de comissao ou sem faixa cadastrada. Nesse caso o campo NAO e tocado: ele e editavel na aba
     * Comissao do modal do item, e sobrescrever apagaria o que o usuario digitou.
     *
     * `emitEvent: false` porque isto e consequencia, nao edicao: emitir reentraria no proprio
     * `valueChanges` que disparou o calculo.
     */
    private aplicarComissao(linha: FormGroup): void {
        const percentual = percentualDaComissao(
            this.vendedorAtual,
            linha.get('dados.precoBrutoUnitario')?.value,
            linha.get('dados.precoLiquidoUnitario')?.value,
            linha.get('dados.descontoPercentual')?.value,
        );

        if (percentual == null) return;

        linha.get('comissao.percentualComissao')?.setValue(percentual, { emitEvent: false });
        recalcularValoresDoItem(linha);
    }

    /**
     * A sequência é o que a impressão usa, então ela acompanha a ordem da tela — e não a ordem
     * de inclusão. Chamada tanto ao excluir quanto ao reordenar pela alça.
     */
    public renumerar(): void {
        // `emitEvent: false`: a sequência é campo de arrumação e não entra em conta nenhuma.
        // Emitindo, cada renumeração dispararia `recalcularValoresDoItem` em TODAS as linhas — trabalho
        // jogado fora ao reordenar, e pior no carregamento: apagaria os valores gravados que a
        // linha carregada acabou de receber, que é justamente o que `ligarRecalculo(linha, false)`
        // existe para preservar.
        this.itens.controls.forEach((linha, indice) =>
            linha.get('dados.sequencia')?.setValue(indice + 1, { emitEvent: false }));
    }
}
